import { useEffect, useState, useRef } from "react";
import { Box, Typography, IconButton, Zoom, Grow, Chip, Backdrop, Button, Icon } from "@mui/material";
import { FiberManualRecord, HighlightOff } from "@mui/icons-material";
import confetti from "canvas-confetti";

const SPLASH_KEY = "atcSplashDismissedAt";
const HIDE_DURATION_HOURS = 24;

const SplashScreen = () => {
    const [visible, setVisible] = useState(false);
    const confettiCanvasRef = useRef(null);
    const confettiShownRef = useRef(false);

    const launchConfetti = () => {
        const canvas = confettiCanvasRef.current;
        if (!canvas) return;

        const duration = 5 * 1000;
        const animationEnd = Date.now() + duration;
        const defaults = {
            startVelocity: 15,
            spread: 460,
            ticks: 100,
            zIndex: 0
        };

        const randomInRange = (min, max) => Math.random() * (max - min) + min;

        const myConfetti = confetti.create(canvas, {
            resize: true,
            useWorker: true
        });



        // Ongoing confetti spray from sides
        const interval = setInterval(() => {
            const timeLeft = animationEnd - Date.now();
            if (timeLeft <= 0) {
                clearInterval(interval);
                return;
            }

            const particleCount = 50 * (timeLeft / duration);
            myConfetti({
                ...defaults,
                particleCount,
                origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
            });
            myConfetti({
                ...defaults,
                particleCount,
                origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
            });
        }, 250);
    };

    useEffect(() => {
        const lastDismissed = localStorage.getItem(SPLASH_KEY);
        if (!lastDismissed) {
            setVisible(true);
        } else {
            const dismissedAt = new Date(lastDismissed);
            const now = new Date();
            const hoursPassed = (now.getTime() - dismissedAt.getTime()) / 1000 / 60 / 60;
            if (hoursPassed >= HIDE_DURATION_HOURS) {
                setVisible(true);
            }
        }
    }, []);

    useEffect(() => {
        if (visible && !confettiShownRef.current) {
            confettiShownRef.current = true;
            launchConfetti();
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
        };
    }, [visible]);

    const handleClose = () => {
        localStorage.setItem(SPLASH_KEY, new Date().toISOString());
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <>
            <canvas
                ref={confettiCanvasRef}
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    pointerEvents: "none",
                    zIndex: 1401
                }}
            />

            <Backdrop
                open={visible}
                sx={{
                    zIndex: theme => theme.zIndex.drawer + 1,
                    backgroundColor: "rgba(0, 0, 0, 0.85)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}>
                <Zoom in={visible} timeout={500}>
                    <Box
                        sx={{
                            width: {
                                xs: "90vw",
                                sm: "80vw",
                                md: "60vw",
                                lg: 500
                            },
                            bgcolor: "background.paper",
                            border: "2px solid #000",
                            boxShadow: 24,
                            borderRadius: 2,
                            px: {
                                xs: 2,
                                sm: 4,
                                md: 5
                            },
                            py: {
                                xs: 8,
                                sm: 8,
                                md: 10
                            },
                            position: "relative",
                            textAlign: "center",
                            overflow: "hidden"
                        }}>
                        <div className="custom-shape-divider-top-1677101761">
                            <svg
                                data-name="Layer 1"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 1200 120"
                                preserveAspectRatio="none">
                                <path
                                    d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
                                    opacity=".25"
                                    className="shape-fill"></path>
                                <path
                                    d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
                                    opacity=".5"
                                    className="shape-fill"></path>
                                <path
                                    d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
                                    className="shape-fill"></path>
                            </svg>
                        </div>
                        <Box
                            sx={{
                                position: "absolute",
                                top: {
                                    xs: 1,
                                    sm: 4
                                },
                                right: {
                                    xs: 1,
                                    sm: 4
                                }
                            }}>
                            <IconButton onClick={handleClose} aria-label="close" size="large">
                                <HighlightOff sx={{ color: "white", fontSize: 28 }} />
                            </IconButton>
                        </Box>

                        <Grow in={visible} timeout={1500}>
                            <Box
                                sx={{
                                    mb: 3,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center"
                                }}>
                                <Box
                                    component="img"
                                    src="/assets/logos/sb-logo-blue.png"
                                    alt="SBC ATC Logo"
                                    sx={{
                                        height: {
                                            xs: 40,
                                            sm: 50,
                                            md: 60
                                        },
                                        mb: 3
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        textTransform: "uppercase",
                                        fontSize: {
                                            xs: "1rem",
                                            sm: "1.25rem",
                                            md: "1.5rem"
                                        },
                                        fontWeight: 700
                                    }}>
                                    Our New Website Is Here!
                                </Typography>
                            </Box>
                        </Grow>

                        <Grow in={visible} timeout={2000}>
                            <Typography
                                variant="body1"
                                sx={{
                                    mb: 4,
                                    fontSize: {
                                        xs: "0.75rem",
                                        sm: "1rem"
                                    }
                                }}>
                                We are excited to announce the launch of the new San Bernardino County
                                Auditor-Controller/Treasurer/Tax Collector (ATC) website. Our fresh new look brings {" "}
                                <em>sbcountyatc.gov</em> and <em>mytaxcollector.com</em> into a single,
                                modern, user-friendly platform featuring enhanced access to key services and
                                centralized tools and resources for both taxpayers and vendors.
                            </Typography>
                        </Grow>

                        <Grow in={visible} timeout={2500}>
                            <Chip
                                icon={<FiberManualRecord color="success" sx={{ fontSize: 14 }} />}
                                label={
                                    <span>
                                        <strong>Visit us at:</strong> sbcountyatc.gov
                                    </span>
                                }
                                sx={{
                                    fontSize: {
                                        xs: "0.75rem",
                                        sm: "1rem"
                                    },
                                    px: 2,
                                    py: 1.5,
                                    borderRadius: 10,
                                    width: "fit-content",
                                    mx: "auto",
                                    mb: 4,
                                    textTransform: "uppercase"
                                }}
                            />
                        </Grow>

                        <Grow in={visible} timeout={3000}>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: {
                                        xs: "0.75rem",
                                        sm: "1rem"
                                    }
                                }}>
                                <strong>Already bookmarked <em>mytaxcollector.com</em></strong>?  No problem, automatic redirects will be in place, but we
                                recommend updating your bookmarks now that the new site is live.
                            </Typography>
                        </Grow>
                        <div className="my-3">
                            <Button
                                onClick={handleClose}
                                size="large"
                                style={{ backgroundColor: "#abd6ff", color: "#036" }} // Light green bg with dark green text
                            >
                                Take Me There 🚀
                            </Button>
                        </div>

                        <div className="custom-shape-divider-bottom-1677101896">
                            <svg
                                data-name="Layer 1"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 1200 120"
                                preserveAspectRatio="none">
                                <path
                                    d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
                                    opacity=".25"
                                    className="shape-fill"></path>
                                <path
                                    d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
                                    opacity=".5"
                                    className="shape-fill"></path>
                                <path
                                    d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
                                    className="shape-fill"></path>
                            </svg>
                        </div>
                    </Box>
                </Zoom>
            </Backdrop>
        </>
    );
};

export default SplashScreen;
